import React, { useState, useEffect, useCallback } from 'react';
import { fetchItemKitsCount, fetchItemsCount, fetchUsersCount, fetchRetailersCount, fetchCountriesCount, fetchStatesCount, fetchDeliveryAddressesCount, fetchSessionDurationStats, SessionDurationStats, fetchUserRegistrationStats, RegistrationStatsResponse, fetchCountriesForDropdown, fetchZeroResultsByCountry } from '../services/api.ts';
import { Package, ShoppingBag, Users, MapPin, Globe, Map, Truck, Clock, BarChart3, Search } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import './DashboardHome.css';

const DashboardHome: React.FC = () => {
  const [stats, setStats] = useState({
    itemKits: 0,
    items: 0,
    users: 0,
    retailers: 0,
    countries: 0,
    states: 0,
    deliveryAddresses: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionStats, setSessionStats] = useState<SessionDurationStats | null>(null);
  const [regStats, setRegStats] = useState<RegistrationStatsResponse | null>(null);
  const [regGroupBy, setRegGroupBy] = useState<'daily' | 'weekly' | 'monthly'>('monthly');
  const [regCountryId, setRegCountryId] = useState<number | undefined>(undefined);
  const [countryList, setCountryList] = useState<{ id: number; name: string }[]>([]);
  const [zeroResData, setZeroResData] = useState<{ countries: string[]; data: Record<string, any>[] }>({ countries: [], data: [] });
  const [zeroResGroupBy, setZeroResGroupBy] = useState<'day' | 'week' | 'month'>('day');

  // Color palette for countries in the chart
  const COUNTRY_COLORS = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899', '#8b5cf6', '#14b8a6', '#f97316', '#64748b'];

  // Transform raw registration data into recharts-friendly format
  const transformRegData = useCallback((data: RegistrationStatsResponse | null) => {
    if (!data || data.data.length === 0) return [];
    // Group by period, with each country as a key
    const periodMap: Record<string, Record<string, number>> = {};
    for (const d of data.data) {
      if (!d.period) continue;
      if (!periodMap[d.period]) periodMap[d.period] = {};
      periodMap[d.period][d.country_name] = (periodMap[d.period][d.country_name] || 0) + d.count;
    }
    return Object.entries(periodMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([period, countries]) => ({
        period: formatPeriodLabel(period, data.group_by),
        ...countries,
      }));
  }, []);

  const formatPeriodLabel = (dateStr: string, groupBy: string) => {
    const d = new Date(dateStr);
    if (groupBy === 'daily') return d.toLocaleDateString('en-AU', { month: 'short', day: 'numeric' });
    if (groupBy === 'weekly') return `W ${d.toLocaleDateString('en-AU', { month: 'short', day: 'numeric' })}`;
    return d.toLocaleDateString('en-AU', { month: 'short', year: 'numeric' });
  };

  // Load registration stats
  const loadRegStats = useCallback(async () => {
    try {
      const data = await fetchUserRegistrationStats(regGroupBy, regCountryId, undefined, undefined, false);
      setRegStats(data);
    } catch (err) {
      console.warn('Registration stats unavailable:', err);
    }
  }, [regGroupBy, regCountryId]);

  useEffect(() => {
    loadRegStats();
  }, [loadRegStats]);

  // Load zero-results-by-country data
  const loadZeroRes = useCallback(async () => {
    try {
      const res = await fetchZeroResultsByCountry({ groupBy: zeroResGroupBy });
      setZeroResData({ countries: res.countries, data: res.data.map(d => ({ ...d, date: formatPeriodLabel(d.date, zeroResGroupBy) })) });
    } catch (err) {
      console.warn('Zero results data unavailable:', err);
    }
  }, [zeroResGroupBy]);

  useEffect(() => {
    loadZeroRes();
  }, [loadZeroRes]);

  useEffect(() => {
    const loadStats = async () => {
      try {
        setLoading(true);
        const [itemKits, items, users, retailers, countries, states, addresses] = await Promise.all([
          fetchItemKitsCount(),
          fetchItemsCount(),
          fetchUsersCount(undefined, undefined, false),
          fetchRetailersCount(),
          fetchCountriesCount(),
          fetchStatesCount(),
          fetchDeliveryAddressesCount()
        ]);

        // Fetch session duration separately (non-blocking)
        fetchSessionDurationStats(30)
          .then(data => setSessionStats(data))
          .catch(err => console.warn('Session stats unavailable:', err));

        // Fetch countries for dropdown (non-blocking)
        fetchCountriesForDropdown()
          .then(data => setCountryList(data || []))
          .catch(err => console.warn('Countries list unavailable:', err));

        setStats({
          itemKits,
          items,
          users,
          retailers,
          countries,
          states,
          deliveryAddresses: addresses
        });
        setError(null);
      } catch (err) {
        console.error('Error loading stats:', err);
        setError('Failed to load statistics');
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, []);

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    if (mins > 0) return `${mins}m ${secs}s`;
    return `${secs}s`;
  };

  if (loading) {
    return (
      <div className="dashboard-home">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-home">
      {error && (
        <div className="error-alert">
          <span>⚠️ {error}</span>
        </div>
      )}

      <div className="stats-grid">
        <div className="stat-card stat-card-primary">
          <div className="stat-icon-wrapper stat-icon-primary">
            <Package size={24} />
          </div>
          <div className="stat-content">
            <h3 className="stat-value">{stats.itemKits.toLocaleString()}</h3>
            <p className="stat-label">Item Kits</p>
          </div>
        </div>

        <div className="stat-card stat-card-success">
          <div className="stat-icon-wrapper stat-icon-success">
            <ShoppingBag size={24} />
          </div>
          <div className="stat-content">
            <h3 className="stat-value">{stats.items.toLocaleString()}</h3>
            <p className="stat-label">Total Items</p>
          </div>
        </div>

        <div className="stat-card stat-card-warning">
          <div className="stat-icon-wrapper stat-icon-warning">
            <Users size={24} />
          </div>
          <div className="stat-content">
            <h3 className="stat-value">{stats.users.toLocaleString()}</h3>
            <p className="stat-label">Total Users</p>
          </div>
        </div>

        <div className="stat-card stat-card-info">
          <div className="stat-icon-wrapper stat-icon-info">
            <Truck size={24} />
          </div>
          <div className="stat-content">
            <h3 className="stat-value">{stats.retailers.toLocaleString()}</h3>
            <p className="stat-label">Retailers</p>
          </div>
        </div>
      </div>

      <div className="stats-row-2">
        <div className="secondary-stat-card">
          <div className="secondary-stat-icon"><Globe size={18} /></div>
          <div className="secondary-stat-content">
            <h4 className="secondary-stat-value">{stats.countries.toLocaleString()}</h4>
            <p className="secondary-stat-label">Countries</p>
          </div>
        </div>
        <div className="secondary-stat-card">
          <div className="secondary-stat-icon"><Map size={18} /></div>
          <div className="secondary-stat-content">
            <h4 className="secondary-stat-value">{stats.states.toLocaleString()}</h4>
            <p className="secondary-stat-label">States</p>
          </div>
        </div>
        <div className="secondary-stat-card">
          <div className="secondary-stat-icon"><MapPin size={18} /></div>
          <div className="secondary-stat-content">
            <h4 className="secondary-stat-value">{stats.deliveryAddresses.toLocaleString()}</h4>
            <p className="secondary-stat-label">Addresses</p>
          </div>
        </div>
        {sessionStats && sessionStats.total_sessions > 0 && (<>
          <div className="secondary-stat-card">
            <div className="secondary-stat-icon secondary-stat-icon-session"><Clock size={18} /></div>
            <div className="secondary-stat-content">
              <h4 className="secondary-stat-value">{formatDuration(sessionStats.avg_duration_seconds)}</h4>
              <p className="secondary-stat-label">Avg Session</p>
            </div>
          </div>
          <div className="secondary-stat-card">
            <div className="secondary-stat-content">
              <h4 className="secondary-stat-value">{sessionStats.total_sessions.toLocaleString()}</h4>
              <p className="secondary-stat-label">Sessions (30d)</p>
            </div>
          </div>
          <div className="secondary-stat-card">
            <div className="secondary-stat-content">
              <h4 className="secondary-stat-value">{sessionStats.avg_pages_per_session}</h4>
              <p className="secondary-stat-label">Pages / Session</p>
            </div>
          </div>
        </>)}
      </div>

      {/* User Registrations Chart */}
      <div className="reg-chart-section">
        <div className="reg-chart-header">
          <div className="reg-chart-title">
            <BarChart3 size={20} />
            <h3>User Registrations</h3>
          </div>
          <div className="reg-chart-controls">
            <select
              className="reg-chart-select"
              value={regCountryId || ''}
              onChange={(e) => setRegCountryId(e.target.value ? parseInt(e.target.value) : undefined)}
            >
              <option value="">All Countries</option>
              {countryList.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <div className="reg-chart-toggle">
              {(['daily', 'weekly', 'monthly'] as const).map((g) => (
                <button
                  key={g}
                  className={regGroupBy === g ? 'active' : ''}
                  onClick={() => setRegGroupBy(g)}
                >
                  {g.charAt(0).toUpperCase() + g.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="reg-chart-body">
          {(() => {
            const chartData = transformRegData(regStats);
            if (chartData.length === 0) {
              return (
                <div className="reg-chart-empty">
                  <Users size={32} />
                  <p>No registration data available</p>
                </div>
              );
            }
            const countries = regStats?.countries || [];
            return (
              <ResponsiveContainer width="100%" height={360}>
                <BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="period" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px' }}
                  />
                  <Legend />
                  {countries.map((country, i) => (
                    <Bar
                      key={country}
                      dataKey={country}
                      fill={COUNTRY_COLORS[i % COUNTRY_COLORS.length]}
                      radius={[4, 4, 0, 0]}
                      stackId="stack"
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            );
          })()}
        </div>
      </div>

      {/* Zero Results by Country Chart */}
      <div className="reg-chart-section">
        <div className="reg-chart-header">
          <div className="reg-chart-title">
            <Search size={20} />
            <h3>Zero Result Searches by Country</h3>
          </div>
          <div className="reg-chart-controls">
            <div className="reg-chart-toggle">
              {(['day', 'week', 'month'] as const).map((g) => (
                <button
                  key={g}
                  className={zeroResGroupBy === g ? 'active' : ''}
                  onClick={() => setZeroResGroupBy(g)}
                >
                  {g === 'day' ? 'Daily' : g === 'week' ? 'Weekly' : 'Monthly'}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="reg-chart-body">
          {zeroResData.data.length === 0 ? (
            <div className="reg-chart-empty">
              <Search size={32} />
              <p>No zero-result search data available</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={360}>
              <LineChart data={zeroResData.data} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px' }} />
                <Legend />
                {zeroResData.countries.map((country, i) => (
                  <Line
                    key={country}
                    type="monotone"
                    dataKey={country}
                    stroke={COUNTRY_COLORS[i % COUNTRY_COLORS.length]}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="info-section">
        <div className="info-card">
          <h3>Quick Stats</h3>
          <div className="info-list">
            <div className="info-item">
              <span className="info-dot info-dot-primary"></span>
              <span>Catalog Management</span>
              <span className="info-value">{stats.itemKits + stats.items} items</span>
            </div>
            <div className="info-item">
              <span className="info-dot info-dot-success"></span>
              <span>User Engagement</span>
              <span className="info-value">{stats.users} users</span>
            </div>
            <div className="info-item">
              <span className="info-dot info-dot-warning"></span>
              <span>Retail Network</span>
              <span className="info-value">{stats.retailers} partners</span>
            </div>
            <div className="info-item">
              <span className="info-dot info-dot-info"></span>
              <span>Geographic Coverage</span>
              <span className="info-value">{stats.countries} countries</span>
            </div>
          </div>
        </div>

        <div className="info-card">
          <h3>System Status</h3>
          <div className="status-list">
            <div className="status-item">
              <span className="status-indicator status-online"></span>
              <span>All Systems Operational</span>
            </div>
            <div className="status-item">
              <span className="status-indicator status-online"></span>
              <span>API Services Running</span>
            </div>
            <div className="status-item">
              <span className="status-indicator status-online"></span>
              <span>Database Connected</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardHome;
